<template>
    <ui-modal ref="modal" title="Lead Won">
        <div v-if="isLoadingPathways || isConvertingLead">
            <div style="display: flex; align-items: center; justify-items: center; min-height: 300px;">
                <ui-progress-circular
                    :stroke="2"
                    type="indeterminate"
                    style="margin:auto"
                    :size="96">
                </ui-progress-circular>
            </div>
        </div>
        <div v-else-if="isSalesLead && hasPathways">
            <div v-if="reservationPathways.length > 0">
                <p>We've found these related reservations:</p>
                <ul class="lead-detail-list">
                    <li
                        @click="select(pathway)"
                        :class="{ 'lead-detail-option': true, 'active': isOptionActive(pathway), 'disabled': isOptionDisabled(pathway) }"
                        v-for="pathway in reservationPathways"
                    >
                        <div style="flex-grow: 1;">
                            <div>
                                Reservation #{{ pathway.using.id }} - {{ pathway.using.contact.primary_display_as }}
                            </div>
                            <div><strong>{{ pathway.using.instance.vehicle.manufacturer.name }} {{ pathway.using.instance.vehicle.model.name }}</strong></div>
                            <div>{{ pathway.using.instance.vehicle.derivative }}</div>
                            <div><span class="registration small">{{ vrm(pathway.using.instance.vehicle) }}</span></div>
                        </div>
<!--                        <div>-->
<!--                            <div :class="{ 'stock-status-label': true, 'in-stock': true }">{{ pathway.using.life_cycle_status_label }}</div>-->
<!--                        </div>-->
                    </li>
                </ul>
            </div>
            <div v-if="wonPathways.length > 0">
                <p>We've found these related deals:</p>
                <ul class="lead-detail-list">
                    <li
                        @click="select(pathway)"
                        :class="{ 'lead-detail-option': true, 'active': isOptionActive(pathway), 'disabled': isOptionDisabled(pathway) }"
                        v-for="pathway in wonPathways"
                    >
                        <div style="flex-grow: 1;">
                            <div>
                                Deal #{{ pathway.using.id }} - {{ pathway.using.contact.primary_display_as }}
                            </div>
                            <div><strong>{{ pathway.using.instance.vehicle.manufacturer.name }} {{ pathway.using.instance.vehicle.model.name }}</strong></div>
                            <div>{{ pathway.using.instance.vehicle.derivative }}</div>
                            <div><span class="registration small">{{ vrm(pathway.using.instance.vehicle) }}</span></div>
                        </div>
                        <div>
                            <div :class="{ 'stock-status-label': true, 'in-stock': true }">{{ pathway.using.life_cycle_status_label }}</div>
                        </div>
                    </li>
                </ul>
            </div>
            <div v-if="convertPathways.length > 0">
                <p>Or you can create a new deal with the vehicle selected below:</p>
                <ul class="lead-detail-list">
                    <li
                        @click="select(pathway)"
                        :class="{ 'lead-detail-option': true, 'active': isOptionActive(pathway), 'disabled': isOptionDisabled(pathway) }"
                        v-for="pathway in convertPathways"
                    >
                        <div style="flex-grow: 1;">
                            <div><strong>{{ pathway.using.detail.vehicle.manufacturer.name }} {{ pathway.using.detail.vehicle.model.name }}</strong></div>
                            <div>{{ pathway.using.detail.vehicle.derivative }}</div>
                        </div>
                        <div>
                            <div><span class="registration small">{{ vrm(pathway.using.detail.vehicle) }}</span></div>
                        </div>
                    </li>
                </ul>
            </div>
        <ui-button
            @click="won"
            v-if="selected"
            :loading="isConvertingLead"
            :disabled="!canConvertLead"
            color="primary"
            icon="monetization_on"
            style="width: 100%;"
        >{{ selected.type === 'won' ? 'Assign Lead' : 'Create Deal' }}</ui-button>
        </div>
        <div v-else-if="isBuyingLead && hasPathways">
            <div v-if="convertPathways.length > 0">
                <p>Which vehicle would you like to bring into stock:</p>
                <ul class="lead-detail-list">
                    <li
                        @click="select(pathway)"
                        :class="{ 'lead-detail-option': true, 'active': isOptionActive(pathway), 'disabled': isOptionDisabled(pathway) }"
                        v-for="pathway in convertPathways"
                    >
                        <div style="flex-grow: 1;">
                            <div><strong>{{ pathway.using.vehicle.title }}</strong></div>
                            <div>{{ pathway.using.vehicle.derivative }}</div>
                        </div>
                        <div>
                            <div><span class="registration small">{{ vrm(pathway.using.vehicle, 'vrm') }}</span></div>
                        </div>
                    </li>
                </ul>
            </div>
            <ui-button
            @click="won"
            v-if="selected"
            :loading="isConvertingLead"
            :disabled="!canConvertLead"
            color="primary"
            icon="monetization_on"
            style="width: 100%;"
        >{{ selected.type === 'won' ? 'Assign Lead' : 'Buy-in Vehicle' }}</ui-button>
        </div>
        <div v-else>
            <hero-error-message title="No Vehicles" detail="Add a vehicle of interest to the lead before trying to convert it to a deal."></hero-error-message>
        </div>

        <add-vehicle-form
            ref="add"
            should-show-more-details
            :should-show-confirmation="false"
            @instance-created="finishBuyingIn"
            @lookup-failure="errorBuyingIn"
            @vehicle-creation-failed="errorBuyingIn"
            @stocklisting-creation-failure="errorBuyingIn"
        ></add-vehicle-form>
    </ui-modal>
</template>

<script>
import {vrm} from "../../filters/vehicle/vrm";
import {VehicleLifeCycleStatuses} from "../../classes/Api/Vehicle";
import {LeadResource, LeadTypes} from "../../classes/Api/Lead";
import {orderBy} from "lodash";
import VehicleBuyModal from "../forms/vehicles/VehicleBuyModal.vue";
import AddVehicleForm from "../forms/vehicles/AddVehicleForm.vue";
import {VehicleReservationResource} from "../../classes/Api/VehicleReservation";

export default {
    components: {AddVehicleForm},
    props: {
        lead: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isConvertingLead: false,
            selected: null,
            pathways: [],
            isLoadingPathways: true,
        }
    },
    computed: {
        isSalesLead() {
            return this.lead.type === LeadTypes.SALES
        },
        isBuyingLead() {
            return this.lead.type === LeadTypes.BUYING
        },
        hasPathways() {
            return this.pathways.length > 0
        },
        canConvertLead() {
            return !!this.selected
        },
        reservationPathways() {
            return this.pathways.filter(pathway => {
                return pathway.type === 'reservation'
            })
        },
        convertPathways() {
            return this.pathways.filter(pathway => {
                return pathway.type === 'convert'
            })
        },
        wonPathways() {
            return this.pathways.filter(pathway => {
                return pathway.type === 'won'
            })
        }
    },
    methods: {
        vrm,
        open() {
            this.selected = null

            this.isLoadingPathways = true

            this.isConvertingLead = false

            LeadResource.pathways(this.lead.id).then(r => {
                this.isLoadingPathways = false

                this.pathways = orderBy(r.data, 'type', 'desc')

                if (this.pathways.length > 0) {
                    this.select(this.pathways[0])
                }
            })

            this.$refs.modal.open()
        },
        close() {
            this.$refs.modal.close()
        },
        isOptionActive(pathway) {
            return this.selected === pathway
        },
        select(pathway) {
            if (!this.isOptionDisabled(pathway)) {
                this.selected = pathway
            }
        },
        isOptionDisabled(pathway) {
            return this.isConvertingLead ||
                this.lead.type === LeadTypes.SALES && pathway.type === 'convert' && pathway.using && pathway.using.detail &&
                pathway.using.detail.life_cycle_status !== VehicleLifeCycleStatuses.IN_STOCK
        },
        won() {
            if (this.lead.type === LeadTypes.SALES) {
                if (this.selected.type === 'convert') {
                    this.createDeal()
                } else if (this.selected.type === 'won') {
                    this.assignLead()
                } else if (this.selected.type === 'reservation') {
                    this.convertReservation()
                }
            } else if(this.lead.type === LeadTypes.BUYING) {
                this.buyIn()
            }
        },
        assignLead() {
            this.isConvertingLead = true

            let data

            if (this.lead.type === LeadTypes.SALES) {
                data = { convert_to: 'deal:' + this.selected.using.id }
            } else if (this.lead.type === LeadTypes.BUYING) {
                data = { convert_to: 'vehicle.instance:' + this.selected.using.id }
            }

            LeadResource.won(this.lead.id, data).then(r => {
                this.$emit('won', r.data)

                this.close()
            }).catch(e => { this.isConvertingLead = false; handleApiError(e); })
        },
        buyIn() {
            this.isConvertingLead = true
            this.$refs.add.openWithDefaults(
                this.selected.using.vehicle.registration?.vrm ?? '',
                '',
                {
                    contact: this.lead.contact,
                    unit_cost: this.selected.using.appraised_value
                }
            )
        },
        errorBuyingIn() {
            this.isConvertingLead = false
            this.$refs.add.$refs.lookup.close()
        },
        finishBuyingIn(instance) {
            this.$root.navigateTo('vehicles/' + instance.id + '/overview')
        },
        createDeal() {
            this.isConvertingLead = true

            LeadResource.convert(this.lead.id, this.selected.using.id).then(r => {
                this.$root.navigateTo('deals/' + r.data.converted_to_id + '/edit')
            }).catch(e => { this.isConvertingLead = false; handleApiError(e); })
        },
        convertReservation() {
            this.isConvertingLead = true

            VehicleReservationResource.convert(this.selected.using.id).then(r => {

                this.isConvertingLead = false

                this.close()

                if (r.data.deal_id) {
                    this.$root.navigateTo('deals/' + r.data.deal_id + '/edit')
                } else {
                    this.$root.navigateTo('vehicles/' + r.data.instance_id + '/overview')
                }
            }).catch(e => { this.isConvertingLead = false; handleApiError(e); })
        }
    }
}
</script>

<style lang="scss" scoped>
    .lead-detail-list {
        list-style: none;
        padding: 0;
        margin-top: 1rem;
        margin-bottom: 1rem;

        .lead-detail-option {
            padding: 1rem 1.5rem;
            border: 1px solid #ddd;
            box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.05);
            border-radius: 5px;
            display: flex;
            margin-bottom: 1rem;
            align-items: center;
            user-select: none;
            cursor: pointer;

            &.active {
                outline: 3px solid #2196f3;
            }

            &.disabled {
                .registration { background-color: #ccc !important; }
            }
        }

        .stock-status-label {
            padding: .25rem .5rem;
            background: crimson;
            color: white;
            border-radius: 3px;
            font-weight: bolder;

            &.in-stock { background-color: forestgreen; }
        }
    }
</style>